import { Language } from "../language.model"
import {
    AudioMedia,
    DocumentMedia,
    GenericInteractiveMedia,
    HTMLFragmentMedia,
    ImageMedia,
    LTIMedia,
    Media,
    VideoMedia,
} from "./media"
import { MuxDataModel } from "../muxData.model"
import { URSService } from "../../services/urs.service"

export interface AssetResponse {
    guid: string
    title: string
    media_type: string
    medias: Media[]
    description?: string
    use_rights?: string
    accessibility_indicators?: string[]
    content_flags?: string[]
    error?: any
}

export class Asset {
    /*
    Business constraint: All medias in the asset are of the same type which also matches self.mediaType.
    Has state.
    It contains the currently selected media.
    Each media is specific to a language.
     */
    public selectedMedia: Media

    constructor(
        public guid: string = "",
        public title: string = "",
        public mediaType: string = "",
        public medias: Media[] = [],
        public description: string = "",
        public permittedUse: string = "",
        public accessibility: string[] = [],
        public contentFlags: string = "",
        public error: any = null
    ) {
        if (this.medias.length) {
            this.selectedMedia = this.medias[0]
        }
    }

    public static fromData(data: AssetResponse, ursService: URSService): Asset {
        if (!data || !ursService) return

        const medias = (data.medias || []).map((media) => Media.fromData(media, ursService))
        const {
            guid,
            title,
            media_type: mediaType,
            description,
            use_rights: permittedUse,
            accessibility_indicators: accessibility,
            content_flags = [],
            error = null,
        } = data

        const contentFlags = content_flags.length
            ? content_flags.join(", ").replace(/\//gi, " / ")
            : ""

        return new Asset(
            guid,
            title,
            mediaType,
            medias,
            description,
            permittedUse,
            accessibility,
            contentFlags,
            error
        )
    }

    public selectLanguage(language: Language = null) {
        if (!language) {
            this.selectedMedia = this.medias[0]
        } else {
            this.selectedMedia = this.medias.find((media: Media) => media.language === language)
        }
    }

    public getSelectedMediaLanguage(): Language {
        if (this.selectedMedia) {
            return this.selectedMedia.language
        } else {
            return null
        }
    }

    public getRemainingMedias(): Media[] {
        return this.medias.filter((media) => media.language !== this.selectedMedia.language)
    }

    public getDefaultPosterImage(): string {
        return this.selectedMedia.getDefaultPosterImage()
    }

    public isDocument(): boolean {
        return this.selectedMedia instanceof DocumentMedia
    }

    public isVideo(): boolean {
        return this.selectedMedia instanceof VideoMedia
    }

    public isAudio(): boolean {
        return this.selectedMedia instanceof AudioMedia
    }

    public isImage(): boolean {
        return this.selectedMedia instanceof ImageMedia
    }

    // Interactive Lesson = LTI = ILP
    public isLTI(): boolean {
        return this.selectedMedia instanceof LTIMedia
    }

    public hasLTI(): boolean {
        return !!this.medias.find((media) => media instanceof LTIMedia)
    }

    public isGenericInteractive(): boolean {
        return this.selectedMedia instanceof GenericInteractiveMedia
    }

    public isHTMLFragment(): boolean {
        return this.selectedMedia instanceof HTMLFragmentMedia
    }

    public getMuxData(): MuxDataModel {
        if (this.isVideo()) {
            return new MuxDataModel(this.title, this.getSelectedMediaLanguage(), this.mediaType)
        }
        return null
    }

    public getGTMVideoJSInfo() {
        if (this.isVideo()) {
            return {
                title: this.title,
                guid: "",
                type: this.mediaType,
            }
        }
        return null
    }
}
